// src/components/Flow/CircuitRunBanner.jsx
import { useNavigate, useLocation } from 'react-router-dom';
import useCircuitRunStore from '../../stores/useCircuitRunStore';
import { isFocusPath, resolveFocusMode } from '../Focus/focusModes';
import './CircuitRunBanner.css';

function fmt(ms) {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Global "a Circuit is running" banner. Visible app-wide except on the runner page itself. */
export default function CircuitRunBanner() {
  const run = useCircuitRunStore(s => s.run);
  const remainingMs = useCircuitRunStore(s => s.remainingMs);
  const navigate = useNavigate();
  const location = useLocation();
  const active = !!run && (run.status === 'running' || run.status === 'paused');
  if (!active) return null;
  // Already on the runner surface — don't stack "tap to go to it" on top of it.
  //
  // `=== '/circuits'` was right when /circuits was the only route that could show
  // the runner. It is not any more: the Focus dispatcher shows CircuitView on
  // /focus and /rituals too while a circuit is RUNNING, so arriving there mid-run
  // stacked the banner on the running circuit.
  //
  // The test is not simply isFocusPath(): with a cloud-PAUSED run the dispatcher
  // shows Freestyle on /focus, and the banner is exactly what gets the user back.
  // So ask the dispatcher's own resolver what this route is showing, then hide
  // only when that is the circuit surface. `run.status === 'running'` is the same
  // signal the dispatcher reads — useCircuitRunStore bridges it into
  // useFlowStore.executionState verbatim (see bridgeToFlowStore/execStateFor).
  const circuitRunning = run.status === 'running';
  if (isFocusPath(location.pathname)
      && resolveFocusMode(location.pathname, { circuitRunning }) === 'circuit') return null;
  const name = run.circuitSnapshot?.name || 'Circuit';
  const stepCount = run.circuitSnapshot?.steps?.length || 0;
  return (
    <button className="btn btn-primary circuit-run-banner" onClick={() => navigate('/circuits')}>
      <span className="circuit-run-banner__pulse" aria-hidden />
      <span className="circuit-run-banner__name">{name}</span>
      <span className="circuit-run-banner__meta">
        Step {run.currentStepIndex + 1} of {stepCount} · {run.phase}
        {run.status === 'paused' ? ' · paused' : ` · ${fmt(remainingMs)}`}
      </span>
    </button>
  );
}
